/**
 * Route computation over a whole navlog: per-leg wind triangle, times,
 * accumulated totals and the "Rest" columns counted back from the
 * destination — exactly the P12–P18 logic of the LBA form.
 *
 * Pure: takes what the pilot typed, returns what the form should show.
 * "Manual" fields (the pilot overrode a computed value) are respected
 * by the caller; this module only ever computes.
 */
import { num, parseWind, parseWca } from './parse.js';
import { windTriangle, magneticHeading, magneticCourse, legMinutes } from './wind.js';
export function computeLeg(input, carriedVar) {
    const tas = num(input.tas);
    const wind = parseWind(input.wind);
    const tc = num(input.tc);
    const ownVar = num(input.var);
    const variation = Number.isFinite(ownVar) ? ownVar : carriedVar;
    let wca = null;
    let gs = null;
    let unflyable = false;
    const triangle = Number.isFinite(tas) && tas > 0 && wind !== null && Number.isFinite(tc);
    if (triangle) {
        const r = windTriangle({ tas, wind: wind, tc });
        if (r) {
            wca = r.wca;
            gs = r.gs;
        }
        else
            unflyable = true;
    }
    // a hand-entered WCA (whiz wheel / booklet ditto rows) always wins
    const manualWca = parseWca(input.wca);
    if (Number.isFinite(manualWca))
        wca = manualWca;
    // a hand-entered GS wins
    const manualGs = num(input.gs);
    if (Number.isFinite(manualGs))
        gs = manualGs;
    const haveVar = Number.isFinite(variation);
    const mh = Number.isFinite(tc) && wca !== null && haveVar ? magneticHeading(tc, wca, variation) : null;
    const mc = Number.isFinite(tc) && haveVar ? magneticCourse(tc, variation) : null;
    const manualTime = num(input.time);
    const dist = num(input.dist);
    const timeMin = Number.isFinite(manualTime)
        ? Math.round(manualTime)
        : gs !== null && Number.isFinite(dist) ? legMinutes(dist, gs) : null;
    return { wca, gs: gs === null ? null : Math.round(gs), mh, mc, timeMin, unflyable, variation: haveVar ? variation : null };
}
export function computeRoute(legs, opts = {}) {
    const out = [];
    let carried = NaN;
    for (const leg of legs) {
        const r = computeLeg(leg, carried);
        if (r.variation !== null)
            carried = r.variation;
        out.push(r);
    }
    let totalDist = 0, totalTime = 0, nDist = 0, nTime = 0, incomplete = false;
    const dists = legs.map(l => num(l.dist));
    const times = out.map(r => r.timeMin);
    dists.forEach((d, i) => {
        const t = times[i];
        if (Number.isFinite(d)) {
            totalDist += d;
            nDist++;
        }
        if (t !== null && t !== undefined) {
            totalTime += t;
            nTime++;
        }
        if (Number.isFinite(d) && (t === null || t === undefined))
            incomplete = true;
    });
    const totalDistNm = nDist ? Math.round(totalDist) : null;
    const totalTimeMin = nTime ? Math.round(totalTime) : null;
    let runD = opts.totalDistOverride ?? totalDist;
    let runT = opts.totalTimeOverride ?? totalTime;
    const restDistNm = [];
    const restTimeMin = [];
    dists.forEach((d, i) => {
        const t = times[i];
        if (Number.isFinite(d) && nDist) {
            runD -= d;
            restDistNm.push(Math.round(runD));
        }
        else
            restDistNm.push(null);
        if (t !== null && t !== undefined && nTime) {
            runT -= t;
            restTimeMin.push(Math.round(runT));
        }
        else
            restTimeMin.push(null);
    });
    return { legs: out, totalDistNm, totalTimeMin, restDistNm, restTimeMin, incomplete };
}
//# sourceMappingURL=route.js.map